import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  GripVertical,
  Languages,
  Loader2,
  Plus,
  Printer,
  Trash2,
} from 'lucide-react';
import { api } from '../lib/api';
import { useOffer, useOfferMutations, downloadPdf, offersKey } from '../hooks/useOffers';
import ServicePicker from '../components/ServicePicker';
import OfferPreview from '../components/OfferPreview';
import { formatPrice, sizeLabel, statusLabel, STATUS_KEYS } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { OfferDto, OfferItemDto, OfferStatus, PriceDisplay } from '@shared/types';

export default function OfferEditor() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { t, lang: uiLang } = useI18n();
  const qc = useQueryClient();
  const { data: offer, isLoading } = useOffer(id);
  const m = useOfferMutations(id);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Přetahování položek: `dragId` je tažená položka, `overId` ta, nad kterou visí.
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const duplicate = useMutation({
    mutationFn: () => api.post<{ data: OfferDto }>(`/offers/${id}/duplicate`).then((r) => r.data),
    onSuccess: (o) => {
      qc.invalidateQueries({ queryKey: offersKey });
      navigate(`/offers/${o.id}`);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/offers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: offersKey });
      navigate('/');
    },
  });

  const pdf = useMutation({
    mutationFn: () => downloadPdf(id),
    onError: (err: Error & { status?: number }) =>
      setPdfError(err.message || t('editor.pdfFailed', { status: err.status ?? '?' })),
    onSuccess: () => setPdfError(null),
  });

  if (isLoading) return <div className="card p-8 text-sm text-gray-500">{t('common.loading')}</div>;
  if (!offer) return <div className="card p-8 text-sm text-gray-500">{t('editor.notFound')}</div>;

  const field = (key: keyof OfferDto) => ({
    defaultValue: String(offer[key] ?? ''),
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.target.value !== String(offer[key] ?? '')) {
        m.updateOffer.mutate({ [key]: e.target.value } as Partial<OfferDto>);
      }
    },
  });

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/" className="btn-ghost">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <div className="font-heading text-2xl font-bold text-dark">
              {offer.clientName || t('editor.newOffer')}
            </div>
            <div className="text-xs text-gray-500">
              {offer.number} · {statusLabel(t, offer.status)} ·{' '}
              {m.updateOffer.isPending ? t('editor.saving') : t('editor.saved')}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            className="input w-auto"
            value={offer.status}
            onChange={(e) => m.updateOffer.mutate({ status: e.target.value as OfferStatus })}
          >
            {STATUS_KEYS.map((s) => (
              <option key={s} value={s}>
                {statusLabel(t, s)}
              </option>
            ))}
          </select>
          <button className="btn-ghost" onClick={() => duplicate.mutate()} title={t('editor.duplicate')} aria-label={t('editor.duplicate')}>
            <Copy size={16} />
          </button>
          <button
            className="btn-ghost text-danger"
            title={t('common.delete')}
            aria-label={t('common.delete')}
            onClick={() => {
              if (confirm(t('editor.confirmDelete'))) remove.mutate();
            }}
          >
            <Trash2 size={16} />
          </button>
          <a
            className="btn-secondary"
            href={`/api/offers/${id}/preview?v=${offer.updatedAt}`}
            target="_blank"
            rel="noreferrer"
          >
            <Printer size={16} /> {t('editor.print')}
          </a>
          <button className="btn-primary" onClick={() => pdf.mutate()} disabled={pdf.isPending}>
            {pdf.isPending ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            PDF
          </button>
        </div>
      </div>

      {pdfError && (
        <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {pdfError} — {t('editor.pdfFallback')}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,460px)_1fr]">
        {/* ---------------- Levý sloupec: data nabídky ---------------- */}
        <div className="space-y-4">
          <section className="card p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-dark">{t('editor.clientSection')}</h2>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2">
                <span className="label">{t('editor.client')}</span>
                <input className="input" {...field('clientName')} />
              </label>
              <label className="col-span-2">
                <span className="label">{t('editor.property')}</span>
                <input className="input" placeholder="Studio · Binghatti Skyrise" {...field('property')} />
              </label>
              <label>
                <span className="label">{t('editor.unit')}</span>
                <input className="input" placeholder="C2907" {...field('unitNo')} />
              </label>
              <label>
                <span className="label">{t('editor.developer')}</span>
                <input className="input" placeholder="Binghatti" {...field('developer')} />
              </label>
              <label>
                <span className="label">{t('editor.offerDate')}</span>
                <input type="date" className="input" {...field('offerDate')} />
              </label>
              <label>
                <span className="label">{t('editor.validUntil')}</span>
                <input type="date" className="input" {...field('validUntil')} />
              </label>
              <label>
                <span className="label">{t('editor.docLang')}</span>
                <select
                  className="input"
                  value={offer.lang}
                  onChange={(e) => m.updateOffer.mutate({ lang: e.target.value as 'cs' | 'en' })}
                >
                  <option value="cs">{t('common.langCs')}</option>
                  <option value="en">{t('common.langEn')}</option>
                </select>
              </label>
              {offer.items.length > 0 && (
                <div className="col-span-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-gray-600">
                      {t('editor.langSwitchNote')}{' '}
                      {offer.lang === 'cs' ? t('editor.langSwitchNoteCs') : t('editor.langSwitchNoteEn')}.
                    </span>
                    <button
                      className="btn-secondary shrink-0 py-2 text-xs"
                      disabled={m.translate.isPending}
                      onClick={() => {
                        if (confirm(t(`editor.confirmTranslate.${offer.lang}`))) {
                          m.translate.mutate();
                        }
                      }}
                    >
                      {m.translate.isPending ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Languages size={14} />
                      )}
                      {t(`editor.translateTo.${offer.lang}`)}
                    </button>
                  </div>
                  {m.translate.isSuccess && !m.translate.isPending && (
                    <div className="mt-2 text-xs text-positive">
                      {t('editor.translated', {
                        catalog: m.translate.data.fromCatalog,
                        machine: m.translate.data.machine,
                      })}
                    </div>
                  )}
                  {m.translate.isError && (
                    <div className="mt-2 text-xs text-danger">
                      {(m.translate.error as Error).message}
                    </div>
                  )}
                </div>
              )}
              <label>
                <span className="label">{t('common.vatRatePct')}</span>
                <input
                  type="number"
                  className="input"
                  defaultValue={offer.vatRate}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (v !== offer.vatRate) m.updateOffer.mutate({ vatRate: v });
                  }}
                />
              </label>
              <label className="col-span-2">
                <span className="label">{t('editor.priceDisplay')}</span>
                <select
                  className="input"
                  value={offer.priceDisplay}
                  onChange={(e) =>
                    m.updateOffer.mutate({ priceDisplay: e.target.value as PriceDisplay })
                  }
                >
                  <option value="detailed">{t('editor.priceDetailed')}</option>
                  <option value="summary">{t('editor.priceSummary')}</option>
                </select>
                <span className="mt-1 block text-xs text-gray-500">
                  {offer.priceDisplay === 'summary'
                    ? t('editor.priceSummaryHint')
                    : t('editor.priceDetailedHint')}
                </span>
              </label>
            </div>
          </section>

          <section className="card p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-dark">{t('editor.termsSection')}</h2>
            <div className="space-y-3">
              <label className="block">
                <span className="label">{t('common.paymentTerms')}</span>
                <input className="input" {...field('paymentTerms')} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label>
                  <span className="label">{t('common.phone')}</span>
                  <input className="input" {...field('contactPhone')} />
                </label>
                <label>
                  <span className="label">{t('common.email')}</span>
                  <input className="input" {...field('contactEmail')} />
                </label>
              </div>
              <label className="block">
                <span className="label">{t('editor.notes')}</span>
                <textarea className="input" rows={2} {...field('notes')} />
              </label>
            </div>
          </section>

          <section className="card p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-dark">
                {t('editor.services')} <span className="text-sm font-normal text-gray-500">({offer.items.length})</span>
              </h2>
              <button className="btn-secondary py-2 text-xs" onClick={() => setPickerOpen(true)}>
                <Plus size={14} /> {t('editor.addService')}
              </button>
            </div>

            {offer.items.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">
                {t('editor.noServices')}
              </p>
            ) : (
              <div className="space-y-2">
                {offer.items.map((item, index) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    index={index}
                    count={offer.items.length}
                    lang={uiLang}
                    isDragging={dragId === item.id}
                    isOver={overId === item.id && dragId !== item.id}
                    onDragStart={() => setDragId(item.id)}
                    onDragEnter={() => setOverId(item.id)}
                    onDragEnd={() => {
                      setDragId(null);
                      setOverId(null);
                    }}
                    onDrop={() => {
                      if (!dragId || dragId === item.id) return;
                      const order = offer.items.map((i) => i.id);
                      const [moved] = order.splice(order.indexOf(dragId), 1);
                      order.splice(index, 0, moved);
                      m.reorderItems.mutate(order);
                    }}
                    onPatch={(patch) => m.updateItem.mutate({ itemId: item.id, patch })}
                    onRemove={() => m.removeItem.mutate(item.id)}
                    onMove={(dir) => {
                      const order = offer.items.map((i) => i.id);
                      const target = index + dir;
                      if (target < 0 || target >= order.length) return;
                      [order[index], order[target]] = [order[target], order[index]];
                      m.reorderItems.mutate(order);
                    }}
                  />
                ))}
              </div>
            )}

            <div className="mt-4 space-y-1 border-t border-gray-200 pt-4 text-sm">
              <Row label={t('editor.subtotal')} value={formatPrice(offer.totals.subtotal, offer.totals.isFrom, uiLang)} />
              <Row label={t('editor.vat', { rate: offer.vatRate })} value={formatPrice(offer.totals.vat, false, uiLang)} muted />
              <Row
                label={t('editor.total')}
                value={formatPrice(offer.totals.total, offer.totals.isFrom, uiLang)}
                strong
              />
            </div>
          </section>
        </div>

        {/* ---------------- Pravý sloupec: náhled ---------------- */}
        <div className="lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)]">
          <OfferPreview offerId={id} version={offer.updatedAt} />
        </div>
      </div>

      {pickerOpen && (
        <ServicePicker
          lang={offer.lang}
          busy={m.addFromCatalog.isPending}
          onClose={() => setPickerOpen(false)}
          onPick={(payload) => {
            m.addFromCatalog.mutate(payload);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div className={`flex justify-between ${muted ? 'text-gray-500' : ''}`}>
      <span className={strong ? 'font-semibold text-dark' : ''}>{label}</span>
      <span className={strong ? 'font-heading text-lg font-bold text-teal' : 'font-medium'}>
        {value}
      </span>
    </div>
  );
}

function ItemRow({
  item,
  index,
  count,
  lang,
  isDragging,
  isOver,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
  onPatch,
  onRemove,
  onMove,
}: {
  item: OfferItemDto;
  index: number;
  count: number;
  lang: 'cs' | 'en';
  isDragging: boolean;
  isOver: boolean;
  onDragStart: () => void;
  onDragEnter: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
  onPatch: (patch: Record<string, unknown>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  // Draggable je zapnuté jen po stisku úchytu — jinak by nešlo označit text v polích.
  const [grabbed, setGrabbed] = useState(false);

  return (
    <div
      draggable={grabbed}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
        setGrabbed(false);
      }}
      onDragEnd={() => {
        setGrabbed(false);
        onDragEnd();
      }}
      className={`rounded-lg border bg-white transition-colors ${
        isDragging ? 'border-teal opacity-40' : isOver ? 'border-teal border-dashed' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center gap-2 p-3">
        <div
          className="flex cursor-grab flex-col items-center text-gray-300 active:cursor-grabbing"
          onMouseDown={() => setGrabbed(true)}
          onMouseUp={() => setGrabbed(false)}
          title={t('editor.dragHint')}
        >
          <GripVertical size={16} className="hover:text-teal" />
          <div className="flex flex-col">
            <button
              className="hover:text-teal disabled:opacity-30"
              disabled={index === 0}
              onClick={() => onMove(-1)}
              aria-label={t('editor.moveUp')}
            >
              <ChevronUp size={12} />
            </button>
            <button
              className="hover:text-teal disabled:opacity-30"
              disabled={index === count - 1}
              onClick={() => onMove(1)}
              aria-label={t('editor.moveDown')}
            >
              <ChevronDown size={12} />
            </button>
          </div>
        </div>

        <button className="min-w-0 flex-1 text-left" onClick={() => setOpen((o) => !o)}>
          <div className="truncate text-sm font-medium text-dark">{item.title}</div>
          <div className="text-xs text-gray-500">
            {item.sizeKey !== 'flat' && `${sizeLabel(t, item.sizeKey)} · `}
            {item.amount === 0 && item.subtitle ? item.subtitle : formatPrice(item.amount, item.isFrom, lang)}
            {item.quantity > 1 && ` × ${item.quantity}`}
            {!item.vatApplicable && ` · ${t('editor.noVat')}`}
            {!item.showDetailPage && ` · ${t('editor.noDetail')}`}
          </div>
        </button>

        <button className="text-gray-300 hover:text-danger" onClick={onRemove} aria-label={t('common.remove')}>
          <Trash2 size={15} />
        </button>
      </div>

      {open && (
        <div className="space-y-3 border-t border-gray-100 bg-gray-50 p-3">
          <label className="block">
            <span className="label">{t('editor.itemTitle')}</span>
            <input
              className="input"
              defaultValue={item.title}
              onBlur={(e) => e.target.value !== item.title && onPatch({ title: e.target.value })}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="label">{t('editor.itemPrice')}</span>
              <input
                type="number"
                className="input"
                defaultValue={item.amount}
                onBlur={(e) =>
                  Number(e.target.value) !== item.amount && onPatch({ amount: Number(e.target.value) })
                }
              />
            </label>
            <label>
              <span className="label">{t('editor.itemQty')}</span>
              <input
                type="number"
                min={1}
                className="input"
                defaultValue={item.quantity}
                onBlur={(e) =>
                  Number(e.target.value) !== item.quantity &&
                  onPatch({ quantity: Number(e.target.value) })
                }
              />
            </label>
          </div>
          <label className="block">
            <span className="label">{t('editor.itemSubtitle')}</span>
            <input
              className="input"
              defaultValue={item.subtitle}
              onBlur={(e) => e.target.value !== item.subtitle && onPatch({ subtitle: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="label">{t('common.lead')}</span>
            <textarea
              className="input"
              rows={3}
              defaultValue={item.lead}
              onBlur={(e) => e.target.value !== item.lead && onPatch({ lead: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="label">{t('editor.itemIncludes')}</span>
            <textarea
              className="input font-mono text-xs"
              rows={5}
              defaultValue={item.includesMd}
              onBlur={(e) =>
                e.target.value !== item.includesMd && onPatch({ includesMd: e.target.value })
              }
            />
          </label>
          <label className="block">
            <span className="label">{t('editor.itemConditions')}</span>
            <textarea
              className="input font-mono text-xs"
              rows={3}
              defaultValue={item.conditionsMd}
              onBlur={(e) =>
                e.target.value !== item.conditionsMd && onPatch({ conditionsMd: e.target.value })
              }
            />
          </label>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={item.isFrom}
                onChange={(e) => onPatch({ isFrom: e.target.checked })}
              />
              {t('editor.itemIsFrom')}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={item.vatApplicable}
                onChange={(e) => onPatch({ vatApplicable: e.target.checked })}
              />
              {t('editor.itemVat')}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={item.showDetailPage}
                onChange={(e) => onPatch({ showDetailPage: e.target.checked })}
              />
              {t('editor.itemOwnPage')}
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
